import { inject, observer } from 'mobx-react';
import * as React from 'react';

import { ClickTracker } from 'bernie-analytics';
import { ErrorBoundary, ViewLarge, ViewMedium, Viewport, ViewSmall } from 'bernie-view';
import { LocalizedComponentClass, LocalizedComponentProps, LocalizedText, withLocalization } from 'bernie-l10n';

import { CurrentHotelStore } from 'src/common/stores/current-hotel-store';
import { MultiItemStore } from 'stores/multi-item-store';

import { StickyOfferModel } from 'models';

import { UitkIcon, UitkIconSize } from 'uitk-react-icons';
import { UitkPrimaryButton, UitkSecondaryButton } from 'uitk-react-button';
import { UitkSpacing } from 'uitk-react-spacing';

import { FavoriteButton } from 'components/common/shortlist/favorite-button';
import { MessagingCard } from 'components/common/messaging-card/messaging-card';
import { RoomNightlyPrice } from 'components/infosite/rooms-and-rates/room-nightly-price';
import { SharedUiHeaderSummary } from 'src/common/components/infosite/shared-ui/details/header-summary-wrapper';
import { ShareMenu } from 'src/common/components/infosite/share/share-menu';
import { StickyBookBar } from 'components/infosite/template-components/sticky-book-bar';

interface InformationContentProps {
  withPrice?: boolean;
  navigateTabs?: () => any;
  stickyOffer?: StickyOfferModel;
  multiItem?: MultiItemStore;
}

interface InformationOfferProps {
  currentHotel?: CurrentHotelStore;
}

export const Information: React.FunctionComponent<InformationOfferProps> = inject('currentHotel')(
  observer(({ currentHotel }) => (
    <React.Fragment>
      <Viewport>
        <ViewSmall>
          <UitkSpacing padding={{ blockend: 'three' }} border={['blockend']}>
            <div>
              <SharedUiHeaderSummary />
            </div>
          </UitkSpacing>
        </ViewSmall>
        <ViewLarge>
          <UitkSpacing padding={{ blockend: 'three' }}>
            <div>
              <SharedUiHeaderSummary />
            </div>
          </UitkSpacing>
        </ViewLarge>
      </Viewport>
      {PropertyBanner(currentHotel)}
    </React.Fragment>
  ))
);

const ButtonTracked = ClickTracker(UitkPrimaryButton);

export const InformationLarge: React.FunctionComponent<InformationContentProps & InformationOfferProps> = props => (
  <div className="uitk-grid">
    <UitkSpacing padding={{ blockend: 'three' }}>
      <div className="uitk-cell all-cell-fill">
        <SharedUiHeaderSummary />
      </div>
    </UitkSpacing>
    {Boolean(props.navigateTabs) && (
      <Viewport>
        <ViewSmall />
        <ViewMedium>
          <div className="uitk-cell all-cell-shrink all-l-padding-three">
            <div className="uitk-grid all-grid-align-middle uitk-type-right all-cell-1-4">
              <ErrorBoundary label="hotel.information.details.leadprice.large">
                <span className="all-r-padding-two" data-stid="favorite-save-button">
                  <FavoriteSaveButton currentHotel={props.currentHotel} />
                </span>
                <ButtonTracked onClick={props.navigateTabs} className="book-now-tabbed" rfrr=".GoToReserveTab" linkName="Go To Reserve">
                  <LocalizedText message="hotels.infosite.stickyBookNow.priceDisplay.bookButton" />
                </ButtonTracked>
              </ErrorBoundary>
            </div>
          </div>
        </ViewMedium>
      </Viewport>
    )}
    {!props.navigateTabs && (
      <div className="uitk-cell all-cell-shrink all-l-padding-three">
        {props.currentHotel?.searchCriteria?.isDateless && props.stickyOffer && (
          <RoomNightlyPrice
            prefix={props.stickyOffer && props.stickyOffer.prefix}
            price={props.stickyOffer && props.stickyOffer.price}
            subText={props.stickyOffer && props.stickyOffer.subText}
            qualifier={props.stickyOffer && props.stickyOffer.qualifier}
          />
        )}

        <div className="uitk-flex uitk-flex-justify-content-flex-end">
          <ErrorBoundary label="hotel.information.details.leadprice.large">
            <ShareMenu />
            <span className="all-r-padding-two" data-stid="favorite-save-button">
              <FavoriteSaveButton currentHotel={props.currentHotel} data-stid="favorite-save-button" />
            </span>
            <StickyBookBar />
          </ErrorBoundary>
        </div>
      </div>
    )}
    {PropertyBanner(props.currentHotel)}
  </div>
);
InformationLarge.displayName = 'InformationLarge';

export interface FavoriteSaveButtonProps extends LocalizedComponentProps {
  currentHotel?: CurrentHotelStore;
}

export const FavoriteSaveButton = withLocalization((props: FavoriteSaveButtonProps) => {
  const { currentHotel, l10n } = props;
  const shortlistModel = currentHotel?.hotel?.shortlistModel;
  const buttonText = l10n.formatText('hotels.shortlist.save.button.title');

  if (shortlistModel?.meta?.hotelId) {
    return (
      <FavoriteButton
        shortlistModel={shortlistModel}
        labelSave={l10n.formatText('hotels.infosite.shortlist.saveToList.a11yLabel')}
        labelRemove={l10n.formatText('hotels.infosite.shortlist.removeFromList.a11yLabel')}
        category="hotel"
        onSaveMessage={l10n.formatText('hotels.shortlist.saveToList.notification', currentHotel?.hotel?.hotelName)}
        id={`favorite-${currentHotel?.hotelId}`}
      >
        {(id, label, isChecked, onChange, rfrr, linkName) => (
          <SaveButton id={id} text={buttonText} isChecked={isChecked} onChange={onChange} rfrr={rfrr} linkName={linkName} />
        )}
      </FavoriteButton>
    );
  }

  return null;
}) as LocalizedComponentClass<FavoriteSaveButtonProps>;
FavoriteSaveButton.displayName = 'FavoriteSaveButton';

const SaveButtonSmallDevices = props => (
  <button className="uitk-link uitk-link-layout-default uitk-type-300 all-l-margin-three" type="button" {...props}>
    {props.children}
  </button>
);

export interface SaveButtonProps extends LocalizedComponentProps {
  id?: string;
  isChecked?: boolean;
  text?: string;
  onChange?: () => void;
  rfrr?: string;
  linkName?: string;
}

const TrackedSaveButton = ClickTracker(UitkSecondaryButton);
const TrackedSaveButtonSmall = ClickTracker(SaveButtonSmallDevices);

export const SaveButton = (props: SaveButtonProps) => {
  const icon = props.isChecked ? 'favorite' : 'favorite_border';

  return (
    <Viewport>
      <ViewSmall>
        <TrackedSaveButtonSmall id={props.id} rfrr={props.rfrr} linkName={props.linkName} onClick={props.onChange}>
          <UitkIcon name={icon} />
          {props.text}
        </TrackedSaveButtonSmall>
      </ViewSmall>
      <ViewLarge>
        <TrackedSaveButton
          iconId={props.id}
          icon={icon}
          text={props.text}
          onClick={props.onChange}
          rfrr={props.rfrr}
          linkName={props.linkName}
        />
      </ViewLarge>
    </Viewport>
  );
};
SaveButton.displayName = 'SaveButton';

export const PropertyBanner = currentHotel => {
  const propertyBannerModel = currentHotel?.offers?.legalBanner?.model || currentHotel?.offers?.propertyBannerModel?.model;
  if (!propertyBannerModel) {
    return null;
  }

  return (
    <MessagingCard
      testId="property-banner"
      model={propertyBannerModel}
      withBorder={true}
      iconSize={UitkIconSize.SMALL}
      tag="section"
      className="uitk-cell all-t-padding-three xl-x-padding-zero l-x-padding-zero m-x-padding-zero"
    />
  );
};
